import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Home, Users, FileText, DollarSign, UserCog, Settings, LogOut, Loader2 } from "lucide-react";
import { crm } from "../../lib/crmApi";
import { inputCls } from "../../components/market/admin/ui";
import { CrmHome } from "../../components/crm/CrmHome";
import { Contacts } from "../../components/crm/Contacts";
import { Quotes } from "../../components/crm/Quotes";
import { Sales } from "../../components/crm/Sales";
import { Team } from "../../components/crm/Team";
import { CrmSettings } from "../../components/crm/CrmSettings";

const Ctx = createContext(null);
export const useCrm = () => useContext(Ctx);
const key = (slug) => `crm_auth_${slug}`;

const Login = ({ slug, onLogin }) => {
  const [org, setOrg] = useState(null);
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    crm(`/o/${slug}`).then(setOrg).catch((e) => setErr(e.message));
  }, [slug]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      onLogin(await crm(`/o/${slug}/login`, { method: "POST", body: f }));
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };
  const creator = async () => {
    setBusy(true);
    try {
      onLogin(await crm(`/admin/orgs/${org.id}/access`, { method: "POST" }));
    } catch (e2) {
      setErr(e2.message);
      setBusy(false);
    }
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0b1530] p-5">
      <form onSubmit={submit} className="w-full max-w-sm space-y-3 rounded-3xl bg-white p-7 shadow-2xl" data-testid="crm-login-form">
        {org?.theme?.logo ? <img src={org.theme.logo} alt="" className="mb-2 h-12 w-auto object-contain" /> : null}
        <h1 className="text-xl font-bold text-slate-900">{org?.name || "CRM"}</h1>
        <p className="text-sm text-slate-500">Entra con tu correo y contraseña.</p>
        <input required type="email" className={inputCls} placeholder="Correo" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="crm-email-input" />
        <input required type="password" className={inputCls} placeholder="Contraseña" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="crm-password-input" />
        {err && <p className="text-sm text-red-600" data-testid="crm-login-error">{err}</p>}
        <button disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold text-white" style={{ background: org?.theme?.primary || "#2563EB" }} data-testid="crm-login-submit">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Entrar
        </button>
        {org && localStorage.getItem("uai_creator") && (
          <button type="button" onClick={creator} disabled={busy} className="w-full rounded-xl border border-slate-200 py-3 text-sm font-semibold text-slate-700" data-testid="crm-creator-access">
            Entrar como creador
          </button>
        )}
      </form>
    </div>
  );
};

const TABS = [
  ["home", "Inicio", Home, CrmHome],
  ["contacts", "Clientes", Users, Contacts],
  ["quotes", "Cotizar", FileText, Quotes],
  ["sales", "Ventas", DollarSign, Sales],
  ["team", "Equipo", UserCog, Team, true],
  ["settings", "Ajustes", Settings, CrmSettings, true],
];

export default function CrmApp() {
  const { slug } = useParams();
  const [auth, setAuth] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(key(slug)));
    } catch {
      return null;
    }
  });
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("home");
  const [openContact, setOpenContact] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem(key(slug));
    setAuth(null);
    setMe(null);
    setTab("home");
  }, [slug]);
  const api = useCallback((path, opts = {}) => crm(`/app/${auth.org_id}${path}`, { ...opts, token: auth.token }).catch((e) => {
    if (e.status === 401) logout();
    throw e;
  }), [auth, logout]);
  const reloadMe = useCallback(() => api("/me").then(setMe), [api]);
  useEffect(() => {
    if (auth) reloadMe().catch(() => {});
  }, [auth]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    document.documentElement.style.setProperty("--crm", me?.org?.theme?.primary || "#2563EB");
  }, [me]);

  if (!auth)
    return (
      <Login
        slug={slug}
        onLogin={(d) => {
          localStorage.setItem(key(slug), JSON.stringify(d));
          setAuth(d);
        }}
      />
    );
  if (!me)
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-7 w-7 animate-spin text-slate-400" />
      </div>
    );

  const isOwner = me.user.role === "owner";
  const tabs = TABS.filter((t) => !t[4] || isOwner);
  const Active = tabs.find((t) => t[0] === tab)?.[3] || CrmHome;
  const primary = me.org.theme?.primary || "#2563EB";
  const go = (t, contactId) => {
    setTab(t);
    setOpenContact(contactId || null);
  };

  return (
    <Ctx.Provider value={{ api, me, isOwner, primary, reloadMe, go, openContact, setOpenContact }}>
      <div className="min-h-screen bg-slate-50 pb-24 md:pb-8 md:pl-56" style={{ "--crm": primary }}>
        <aside className="fixed inset-y-0 left-0 z-30 hidden w-56 flex-col border-r border-slate-200 bg-white p-4 md:flex">
          <div className="mb-6 flex items-center gap-2">
            {me.org.theme?.logo ? <img src={me.org.theme.logo} alt="" className="h-9 w-auto max-w-[150px] object-contain" /> : <p className="truncate font-bold text-slate-900">{me.org.name}</p>}
          </div>
          {tabs.map(([v, l, Icon]) => (
            <button key={v} onClick={() => go(v)} className={`mb-1 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium ${tab === v ? "text-white" : "text-slate-600 hover:bg-slate-100"}`} style={tab === v ? { background: primary } : {}} data-testid={`crm-side-${v}`}>
              <Icon className="h-4 w-4" /> {l}
            </button>
          ))}
          <div className="mt-auto border-t border-slate-100 pt-3">
            <p className="truncate text-sm font-medium text-slate-800">{me.user.name}</p>
            <p className="text-xs text-slate-500">{isOwner ? "Administrador" : "Vendedor"}</p>
            <button onClick={logout} className="mt-2 flex items-center gap-2 text-xs text-slate-500" data-testid="crm-logout-side"><LogOut className="h-3.5 w-3.5" /> Salir</button>
          </div>
        </aside>
        <header className="safe-top sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur md:hidden">
          <div className="flex items-center gap-3 px-4 py-3">
            {me.org.theme?.logo ? <img src={me.org.theme.logo} alt="" className="h-8 w-auto max-w-[130px] object-contain" /> : <span className="flex h-8 w-8 items-center justify-center rounded-lg font-bold text-white" style={{ background: primary }}>{me.org.name[0]}</span>}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-slate-900">{me.org.name}</p>
              <p className="truncate text-[11px] text-slate-500">{me.user.name} · {isOwner ? "Administrador" : "Vendedor"}</p>
            </div>
            <button onClick={logout} className="rounded-full p-2 text-slate-500" data-testid="crm-logout" aria-label="Salir"><LogOut className="h-4 w-4" /></button>
          </div>
        </header>
        <main className="mx-auto max-w-5xl px-4 py-5" data-testid={`crm-view-${tab}`}>
          <Active />
        </main>
        <nav className="fixed inset-x-0 bottom-0 z-30 flex border-t border-slate-200 bg-white/95 backdrop-blur md:hidden" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
          {tabs.map(([v, l, Icon]) => (
            <button key={v} onClick={() => go(v)} className="flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium" style={{ color: tab === v ? primary : "#64748B" }} data-testid={`crm-tab-${v}`}>
              <Icon className="h-5 w-5" /> {l}
            </button>
          ))}
        </nav>
      </div>
    </Ctx.Provider>
  );
}
